import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from 'lz-string';

import {
  getHealthCheckId,
  HEALTH_CHECKS,
  type HealthCheckConfig,
  healthCheckPagePath,
  isPageViewGroup,
  isRestHealthCheck,
} from './health-check.config';

/**
 * A request carried in a health-check deeplink — the playground seeds its
 * editors from it instead of the check's configured payload. For a REST
 * check, `variables` carries the edited JSON body.
 */
export type SharedRequest = {
  query?: string;
  variables?: Record<string, unknown>;
};

/**
 * lz-string-compressed JSON, the dashboard's usual URL-state encoding (see
 * `economic-impact/url-state.ts` and CustomDash's `?filters=`), so
 * `/tools/lzstring-decoder` can read a shared link back by hand.
 */
const encodeSharedRequest = (request: SharedRequest): string =>
  compressToEncodedURIComponent(JSON.stringify(request));

/**
 * A hash is hand-editable, so the payload is shape-checked before anything
 * trusts it: a string `query`, an object `variables`, nothing else honoured.
 */
export const decodeSharedRequest = (raw: string): SharedRequest | undefined => {
  const json = decompressFromEncodedURIComponent(raw);
  if (!json) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const { query, variables } = value as Record<string, unknown>;
  const request: SharedRequest = {};
  if (typeof query === 'string') request.query = query;
  if (
    typeof variables === 'object' &&
    variables !== null &&
    !Array.isArray(variables)
  ) {
    request.variables = variables as Record<string, unknown>;
  }
  return request.query === undefined && request.variables === undefined
    ? undefined
    : request;
};

/**
 * The check a GraphQL root field belongs to. Given the dashboard `page` the
 * request ran on (a pathname such as `/company/diversity`), that page's own
 * check, so the board opens the tab for where it ran. Otherwise the op's own
 * `graphql-api` row, so the playground opens the canonical request rather
 * than one page's payload.
 *
 * `checks` narrows the search to what the target board shows: a board leaves
 * out what `healthChecksForEnvironment` drops (deprecated ops and
 * production-only checks, off prod), and a link to a check it does not show
 * opens nothing.
 *
 * `null` when nothing probes the field — mutations (the sweep is read-only),
 * admin-screen queries, and the handful of ops that need an id from an
 * immediately preceding request (`posting_discovery_search_by_id`,
 * `decryptString`).
 */
export const healthCheckIdForRootField = (
  rootField: string,
  {
    page,
    checks = HEALTH_CHECKS,
  }: { page?: string; checks?: readonly HealthCheckConfig[] } = {}
): string | null => {
  const matches = checks.filter(
    (check) =>
      !isRestHealthCheck(check) &&
      (check.publicOp === rootField || check.service === rootField)
  );
  const pagePath = page?.replace(/^\/+|\/+$/g, '');
  const pageCheck = pagePath
    ? matches.find(
        (check) =>
          isPageViewGroup(check.viewGroup) &&
          healthCheckPagePath(check) === pagePath
      )
    : undefined;
  const check =
    pageCheck ??
    matches.find((candidate) => candidate.viewGroup === 'graphql-api') ??
    matches[0];
  return check
    ? getHealthCheckId(check.service, check.viewGroup, check.view)
    : null;
};

/** `#check=<id>`, plus the request when one is carried. */
export const healthCheckHash = (
  checkId: string,
  request?: SharedRequest
): string => {
  const base = `#check=${encodeURIComponent(checkId)}`;
  if (
    !request ||
    (request.query === undefined && request.variables === undefined)
  ) {
    return base;
  }
  return `${base}&req=${encodeSharedRequest(request)}`;
};
